import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useLocalSearchParams } from "expo-router";
import { z } from "zod";

import { FormField } from "~/components/composed/FormField";
import { TurnstileField } from "~/components/TurnstileField";
import {
  AppLink,
  AppText,
  Button,
  ButtonText,
  PasswordField,
  Row,
  Stack,
  TextField,
} from "~/components/ui";
import { AuthScreen } from "~/components/composed/AuthScreen";
import { useAuth } from "~/hooks/useAuth";
import { normalizeAuthRedirectPath } from "~/utils/auth-redirect";
import { loginSchema } from "~/utils/form-schemas";
import { getDevAuthDefaults } from "~/utils/runtime-config";
import { isTurnstileConfigured } from "~/utils/turnstile";
import { COLLECTIONS_PATH } from "~/utils/onboarding";

type LoginValues = z.infer<typeof loginSchema>;

function getLoginErrorMessage(error: unknown) {
  if (!(error instanceof Error)) return "Sign-in failed.";
  if (
    /invalid (login )?credentials|invalid email or password|incorrect/i.test(
      error.message,
    )
  ) {
    return "Email or password is incorrect.";
  }
  return error.message;
}

export default function LoginScreen() {
  const params = useLocalSearchParams<{ callbackURL?: string }>();
  const { login, loginPending } = useAuth();
  const turnstileEnabled = isTurnstileConfigured();
  const devDefaults = getDevAuthDefaults();

  const loginForm = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      email: devDefaults.email,
      password: devDefaults.password,
      captchaToken: "",
    },
  });

  const destinationHint = normalizeAuthRedirectPath(
    params.callbackURL,
    COLLECTIONS_PATH,
  );
  const registerHref = `/register?callbackURL=${encodeURIComponent(destinationHint)}`;

  // Signing in does not navigate from here: the (auth) layout redirects as soon as
  // the session lands, and an account with two-factor on is held by `MfaGate`
  // until the code is entered.
  const submitLogin = loginForm.handleSubmit(async (values) => {
    if (turnstileEnabled && !values.captchaToken) {
      loginForm.setError("captchaToken", {
        message: "Complete the spam check.",
      });
      return;
    }
    try {
      await login({
        email: values.email,
        password: values.password,
        captchaToken: turnstileEnabled
          ? values.captchaToken || undefined
          : undefined,
      });
    } catch (error) {
      loginForm.setError("root", {
        message: getLoginErrorMessage(error),
      });
    }
  });

  return (
    <AuthScreen title="Sign in">
      <Stack>
        {loginForm.formState.errors.root?.message ? (
          <AppText tone="danger" size="xs">
            {loginForm.formState.errors.root.message}
          </AppText>
        ) : null}
        <FormField control={loginForm.control} name="email" label="Email">
          {(field) => (
            <TextField
              {...field}
              value={String(field.value ?? "")}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="email"
              placeholder="you@example.com"
            />
          )}
        </FormField>
        <FormField control={loginForm.control} name="password" label="Password">
          {(field) => (
            <PasswordField
              {...field}
              value={String(field.value ?? "")}
              autoComplete="current-password"
              accessibilityLabel="Password"
            />
          )}
        </FormField>
        <Row between>
          <AppLink href="/forgot-password">Forgot password?</AppLink>
        </Row>
        {turnstileEnabled ? (
          <Stack gap="xs">
            <AppText size="xs" weight="medium">
              Spam protection
            </AppText>
            <TurnstileField
              onToken={(token) =>
                loginForm.setValue("captchaToken", token ?? "", {
                  shouldValidate: true,
                })
              }
            />
            {loginForm.formState.errors.captchaToken?.message ? (
              <AppText tone="danger" size="xs">
                {loginForm.formState.errors.captchaToken.message}
              </AppText>
            ) : null}
          </Stack>
        ) : null}
        <Button onPress={() => void submitLogin()} disabled={loginPending}>
          <ButtonText>{loginPending ? "Signing in..." : "Sign in"}</ButtonText>
        </Button>
        <AppLink href={registerHref as never}>
          Don&apos;t have an account? Create one
        </AppLink>
      </Stack>
    </AuthScreen>
  );
}
